import { useCallback, useEffect, useRef, useState } from "react";

import { losExtractsApi } from "../../../../../api/losExtractsApi";

export const parentOfExtract = (d) => d?.parent || d?.parent_provenance || {};

// Find the extract-index entry for a loan document, matching on the parent
// document id or the source file's storage name (either may line up).
export const findExtractForDoc = (documents, doc) => {
  if (!doc) return null;
  return (
    (documents || []).find((d) => {
      const p = parentOfExtract(d);
      return (
        d?.parent_document_id === doc?._id ||
        (p?.file_storage_name &&
          p.file_storage_name === doc?.file_storage_name)
      );
    }) || null
  );
};

/**
 * Fetches the document-extracts index for a loan
 * (GET /files/extracts/{dealId} via losExtractsApi.getIndex).
 *
 * Returns the parsed `documents` (one entry per source document, with its
 * extract counts + edit state), plus loading/error and a `reload` trigger.
 * Used by the Documents tab to (a) badge the "Extracted Documents" tab with a
 * count and (b) feed the extracted-docs table — both off a single fetch.
 *
 * Pass `enabled: false` to skip the fetch entirely (e.g. when the user isn't on
 * the extracts allowlist) so no needless request is made.
 */
export const useLoanExtractsIndex = (dealId, { enabled = true } = {}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Monotonic request id so a stale in-flight response can't overwrite a newer
  // one (dealId change / rapid reloads).
  const reqRef = useRef(0);

  const reload = useCallback(() => {
    if (!enabled || !dealId) return;
    const req = ++reqRef.current;
    setLoading(true);
    setError(null);
    losExtractsApi
      .getIndex(dealId)
      .then((data) => {
        if (req !== reqRef.current) return;
        setDocuments(Array.isArray(data?.documents) ? data.documents : []);
      })
      .catch((e) => {
        if (req !== reqRef.current) return;
        setError(e);
        setDocuments([]);
      })
      .finally(() => {
        if (req === reqRef.current) setLoading(false);
      });
  }, [dealId, enabled]);

  useEffect(() => {
    if (!enabled || !dealId) {
      setDocuments([]);
      return;
    }
    reload();
  }, [enabled, dealId, reload]);

  return { documents, loading, error, reload };
};

export default useLoanExtractsIndex;
